import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Layers, Users, Lightbulb, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useAuth, useRequireAuth } from "@/context/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

export default function Communities() {
  const { user } = useAuth();
  const requireAuth = useRequireAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/communities").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const join = (c) => requireAuth(async () => {
    const { data } = await api.post(`/communities/${c.id}/join`);
    setList((l) => l.map((x) => (x.id === c.id ? { ...x, joined: data.joined, member_count: data.member_count } : x)));
    toast.success(data.joined ? `Joined ${c.name}` : `Left ${c.name}`);
  });

  const suggest = () => requireAuth(async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/community-suggestions", { name, description: desc, reason });
      toast.success("Suggestion sent for admin review");
      setName(""); setDesc(""); setReason(""); setOpen(false);
    } catch { toast.error("Could not submit"); } finally { setBusy(false); }
  });

  return (
    <div className="px-4 py-4 pb-8">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-medorange-light text-medorange"><Layers className="h-5 w-5" /></span>
        <div>
          <h1 className="font-heading text-xl font-extrabold text-slate-900">Professional Communities</h1>
          <p className="text-xs text-slate-500">Admin-curated circles for verified healthcare professionals</p>
        </div>
      </div>

      <div className="mb-4 flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-500">
        <Lock className="h-4 w-4 shrink-0 text-slate-400" />
        Communities are curated by MedLync admins. You can suggest one below — you can't create it directly.
      </div>

      <button data-testid="community-suggest-modal-button" onClick={() => requireAuth(() => setOpen(true))}
        className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl bg-navy py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-hover">
        <Lightbulb className="h-4 w-4" /> Suggest a Community
      </button>

      <div className="space-y-4">
        {list === null && [1, 2, 3].map((i) => <Skeleton key={i} className="h-36 w-full rounded-2xl" />)}
        {list && list.map((c) => (
          <div key={c.id} data-testid="community-card" className="ml-rise overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div role="button" onClick={() => navigate(`/communities/${c.id}`)} className="cursor-pointer" data-testid={`community-open-${c.id}`}>
              {c.image_url && <img src={fileUrl(c.image_url)} alt="" className="h-24 w-full object-cover" />}
              <div className="p-4 pb-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-heading text-base font-bold text-slate-900">{c.name}</h3>
                    {c.category && <p className="text-[11px] font-semibold uppercase tracking-wide text-medorange">{c.category}</p>}
                  </div>
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                    <Users className="h-3 w-3" /> {c.member_count}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600">{c.description}</p>
              </div>
            </div>
            <div className="px-4 pb-4">
              <Button onClick={() => join(c)} data-testid={`community-join-${c.id}`}
                className={c.joined ? "w-full bg-slate-100 text-slate-700 hover:bg-slate-200" : "w-full bg-medorange text-white hover:bg-medorange-hover"}>
                {c.joined ? "Joined" : "Join Community"}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="suggest-community-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Suggest a Community</DialogTitle>
            <DialogDescription className="sr-only">Propose a new professional community for MedLync admins to review.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Community name" data-testid="suggest-name-input" />
            <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What is it about?" className="min-h-[80px]" />
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why should MedLync curate this?" className="min-h-[60px]" />
            <Button onClick={suggest} disabled={busy} className="w-full bg-medorange hover:bg-medorange-hover" data-testid="suggest-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit for review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
